/**
 * EnhancedAnalytics - Advanced analytics dashboard with customer segmentation,
 * product performance, campaign ROI, time insights, and export functionality
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Users, Package, Target, Clock, Download, TrendingUp, TrendingDown,
  Crown, UserMinus, UserPlus, AlertTriangle, DollarSign, BarChart3,
  Calendar, RefreshCw, ChevronDown, ChevronUp, FileDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/utils/apiClient';

export default function EnhancedAnalytics() {
  const [activeSection, setActiveSection] = useState('segments');
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [segments, setSegments] = useState(null);
  const [products, setProducts] = useState(null);
  const [campaignROI, setCampaignROI] = useState(null);
  const [timeInsights, setTimeInsights] = useState(null);
  
  // Expanded sections
  const [expandedSegment, setExpandedSegment] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const days = parseInt(dateRange);
    
    try {
      const [segRes, prodRes, roiRes, timeRes] = await Promise.all([
        api.get('/admin/analytics/customer-segments', { params: { days } }),
        api.get('/admin/analytics/product-performance', { params: { days } }),
        api.get('/admin/analytics/campaign-roi', { params: { days } }),
        api.get('/admin/analytics/time-insights', { params: { days } })
      ]);
      
      setSegments(segRes.data);
      setProducts(prodRes.data);
      setCampaignROI(roiRes.data);
      setTimeInsights(timeRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportReport = async (type) => {
    try {
      const endpoint = {
        sales: `/admin/analytics/export/sales?days=${dateRange}`,
        customers: `/admin/analytics/export/customers`,
        products: `/admin/analytics/export/products?days=${dateRange}`
      }[type];
      
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} report downloaded`);
    } catch (err) {
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (v) => `$${(v || 0).toFixed(2)}`;

  const segmentConfig = {
    vip: { icon: Crown, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'VIP Customers', desc: 'High value, loyal customers' },
    regular: { icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Regular', desc: 'Active customers' },
    new: { icon: UserPlus, color: 'text-green-500', bg: 'bg-green-500/10', label: 'New', desc: 'Recent first-time customers' },
    at_risk: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'At Risk', desc: 'No activity in 31-60 days' },
    lost: { icon: UserMinus, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Lost', desc: 'No activity in 60+ days' }
  };

  if (loading && !segments) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enhanced-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Advanced Analytics</h2>
          <p className="text-muted-foreground">Deep insights for better business decisions</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
        {[
          { id: 'segments', label: 'Customer Segments', icon: Users },
          { id: 'products', label: 'Product Performance', icon: Package },
          { id: 'campaigns', label: 'Campaign ROI', icon: Target },
          { id: 'time', label: 'Time Insights', icon: Clock },
          { id: 'export', label: 'Export Reports', icon: Download }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeSection === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Customer Segments Section */}
      {activeSection === 'segments' && segments && (
        <div className="space-y-4">
          {/* Segment Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(segmentConfig).map(([key, config]) => {
              const stats = segments.stats?.[key] || { count: 0, total_spent: 0 };
              return (
                <button
                  key={key}
                  onClick={() => setExpandedSegment(expandedSegment === key ? null : key)}
                  className={`p-4 rounded-xl border border-border hover:border-primary/50 transition-all text-left ${
                    expandedSegment === key ? 'ring-2 ring-primary' : ''
                  }`}
                  data-testid={`segment-${key}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center mb-2`}>
                    <config.icon className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <p className="text-2xl font-bold text-foreground">{stats.count}</p>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                  <p className="text-xs font-medium text-primary mt-1">{formatCurrency(stats.total_spent)}</p>
                </button>
              );
            })}
          </div>

          {/* Expanded Segment Details */}
          {expandedSegment && segments.segments?.[expandedSegment] && (
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {(() => {
                    const cfg = segmentConfig[expandedSegment];
                    return (
                      <>
                        <cfg.icon className={`w-5 h-5 ${cfg.color}`} />
                        <h3 className="font-medium">{cfg.label}</h3>
                        <span className="text-muted-foreground text-sm">- {cfg.desc}</span>
                      </>
                    );
                  })()}
                </div>
                <button onClick={() => setExpandedSegment(null)} className="text-muted-foreground hover:text-foreground">
                  <ChevronUp className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {segments.segments[expandedSegment].map((customer, i) => (
                  <div key={customer.contact_id || i} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{customer.name}</p>
                      <p className="text-xs text-muted-foreground">{customer.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">{formatCurrency(customer.total_spent)}</p>
                      <p className="text-xs text-muted-foreground">{customer.total_orders} orders</p>
                    </div>
                  </div>
                ))}
                {segments.segments[expandedSegment].length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No customers in this segment</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Product Performance Section */}
      {activeSection === 'products' && products && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Revenue" value={formatCurrency(products.summary?.total_revenue)} icon={DollarSign} />
            <StatCard label="Units Sold" value={products.summary?.total_units_sold} icon={Package} />
            <StatCard label="Total Profit" value={formatCurrency(products.summary?.total_profit)} icon={TrendingUp} />
            <StatCard label="Avg Margin" value={`${products.summary?.avg_margin || 0}%`} icon={BarChart3} />
          </div>

          {/* Most Ordered vs Slow Movers */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Most Ordered */}
            <div className="bg-card rounded-xl border border-border p-4">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Most Ordered
              </h3>
              <div className="space-y-2">
                {products.best_sellers?.slice(0, 7).map((item, i) => (
                  <div key={item.item_id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i < 3 ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                    }`}>
                      {i + 1}
                    </span>
                    {item.image ? (
                      <img src={item.image} alt="" className="w-8 h-8 rounded-lg object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-muted" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.units_sold} sold</p>
                    </div>
                    <span className="text-sm font-bold text-primary">{formatCurrency(item.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Slow Movers */}
            <div className="bg-card rounded-xl border border-border p-4">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-500" />
                Slow Movers (Consider Promoting)
              </h3>
              <div className="space-y-2">
                {products.slow_movers?.slice(0, 7).map((item, i) => (
                  <div key={item.item_id} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    {item.image ? (
                      <img src={item.image} alt="" className="w-8 h-8 rounded-lg object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-muted" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.units_sold} sold</p>
                    </div>
                    <span className="text-sm text-muted-foreground">{formatCurrency(item.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Profit Items */}
          <div className="bg-card rounded-xl border border-border p-4">
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Top Profit Generators
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {products.top_profit?.slice(0, 5).map((item) => (
                <div key={item.item_id} className="p-3 bg-muted rounded-lg text-center">
                  <p className="font-medium text-sm truncate">{item.name}</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(item.profit)}</p>
                  <p className="text-xs text-muted-foreground">{item.margin}% margin</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Campaign ROI Section */}
      {activeSection === 'campaigns' && campaignROI && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Signups" value={campaignROI.summary?.total_signups} icon={Users} />
            <StatCard label="Codes Redeemed" value={campaignROI.summary?.total_codes_redeemed} icon={Target} />
            <StatCard label="Conversion Rate" value={`${campaignROI.summary?.overall_conversion_rate || 0}%`} icon={TrendingUp} />
            <StatCard label="Campaign Revenue" value={formatCurrency(campaignROI.summary?.total_campaign_revenue)} icon={DollarSign} />
          </div>

          {/* Campaign Table */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-medium text-foreground">Campaign Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Campaign</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Signups</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Redeemed</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Conv. Rate</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Orders</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {campaignROI.campaigns?.map((campaign) => (
                    <tr key={campaign.campaign_id} className="hover:bg-muted/30">
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-sm">{campaign.campaign_name}</p>
                          <p className="text-xs text-muted-foreground">{campaign.discount_percent}% discount</p>
                        </div>
                      </td>
                      <td className="p-3 text-right font-medium">{campaign.total_signups}</td>
                      <td className="p-3 text-right">{campaign.codes_redeemed}</td>
                      <td className="p-3 text-right">
                        <span className={campaign.conversion_rate > 10 ? 'text-green-600' : 'text-muted-foreground'}>
                          {campaign.conversion_rate}%
                        </span>
                      </td>
                      <td className="p-3 text-right">{campaign.orders_generated}</td>
                      <td className="p-3 text-right font-bold text-primary">{formatCurrency(campaign.revenue_generated)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Time Insights Section */}
      {activeSection === 'time' && timeInsights && (
        <div className="space-y-6">
          {/* Key Insights */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card rounded-xl border border-border p-4">
              <p className="text-sm text-muted-foreground">Busiest Day</p>
              <p className="text-xl font-bold text-foreground">{timeInsights.insights?.busiest_day || '-'}</p>
              <p className="text-xs text-primary">{timeInsights.insights?.busiest_day_orders} orders</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4">
              <p className="text-sm text-muted-foreground">Slowest Day</p>
              <p className="text-xl font-bold text-foreground">{timeInsights.insights?.slowest_day || '-'}</p>
              <p className="text-xs text-muted-foreground">{timeInsights.insights?.slowest_day_orders} orders</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4">
              <p className="text-sm text-muted-foreground">Peak Hour</p>
              <p className="text-xl font-bold text-foreground">{timeInsights.insights?.peak_hour || '-'}</p>
              <p className="text-xs text-primary">{timeInsights.insights?.peak_hour_orders} orders</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4">
              <p className="text-sm text-muted-foreground">Rush Hours</p>
              <p className="text-lg font-bold text-foreground">{timeInsights.insights?.rush_hours?.join(', ') || '-'}</p>
              <p className="text-xs text-muted-foreground">Schedule extra staff</p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Daily Breakdown */}
            <div className="bg-card rounded-xl border border-border p-4">
              <h3 className="font-medium text-foreground mb-4">Orders by Day of Week</h3>
              <div className="space-y-2">
                {timeInsights.daily_breakdown?.map((day) => {
                  const maxOrders = Math.max(...(timeInsights.daily_breakdown?.map(d => d.orders) || [1]));
                  const width = maxOrders > 0 ? (day.orders / maxOrders * 100) : 0;
                  return (
                    <div key={day.day} className="flex items-center gap-3">
                      <span className="w-20 text-sm text-muted-foreground">{day.day.slice(0, 3)}</span>
                      <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <span className="w-16 text-right text-sm font-medium">{day.orders}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Hourly Breakdown */}
            <div className="bg-card rounded-xl border border-border p-4">
              <h3 className="font-medium text-foreground mb-4">Orders by Hour</h3>
              <div className="h-48 flex items-end gap-0.5">
                {timeInsights.hourly_breakdown?.map((hour) => {
                  const maxOrders = Math.max(...(timeInsights.hourly_breakdown?.map(h => h.orders) || [1]));
                  const height = maxOrders > 0 ? (hour.orders / maxOrders * 100) : 0;
                  return (
                    <div 
                      key={hour.hour}
                      className="flex-1 flex flex-col items-center group"
                      title={`${hour.label}: ${hour.orders} orders`}
                    >
                      <div 
                        className={`w-full rounded-t transition-colors ${
                          hour.orders > maxOrders * 0.7 ? 'bg-primary' :
                          hour.orders > maxOrders * 0.4 ? 'bg-primary/60' :
                          'bg-primary/30'
                        }`}
                        style={{ height: `${height}%`, minHeight: hour.orders > 0 ? '4px' : '0px' }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>12am</span>
                <span>6am</span>
                <span>12pm</span>
                <span>6pm</span>
                <span>12am</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Section */}
      {activeSection === 'export' && (
        <div className="grid md:grid-cols-3 gap-4">
          <ExportCard
            title="Sales Report"
            description="Order history, totals, discounts applied"
            icon={DollarSign}
            onExport={() => exportReport('sales')}
          />
          <ExportCard
            title="Customer Report"
            description="Contact list with order history"
            icon={Users}
            onExport={() => exportReport('customers')}
          />
          <ExportCard
            title="Product Report"
            description="Product performance and profitability"
            icon={Package}
            onExport={() => exportReport('products')}
          />
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="text-xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Export Card Component
function ExportCard({ title, description, icon: Icon, onExport }) {
  return (
    <div className="bg-card rounded-xl border border-border p-6 text-center">
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <h3 className="font-medium text-foreground mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4">{description}</p>
      <Button onClick={onExport} className="w-full gap-2">
        <FileDown className="w-4 h-4" />
        Download CSV
      </Button>
    </div>
  );
}
